/**
 * Pure helpers shared by the Access-device surfaces (Secure Access → Devices
 * and the per-user Access Devices tab, VSNP-1163). Kept out of the component
 * file so react-refresh sees only components there.
 */

import type {
  AccessDevice,
  AccessDeviceStatus,
  DeviceSubject,
  TokenBindingResult,
} from '../services/access.service';

/** Local, dependency-free date formatting — null-safe. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Map a device status to the StatusBadge vocabulary (which drives its colour). */
export function deviceStatusLabel(status: AccessDeviceStatus): string {
  switch (status) {
    case 'pending_approval':
      return 'pending';
    case 'active':
      return 'active';
    case 'revoked':
      return 'revoked';
    case 'retired':
      return 'inactive';
    default:
      return status;
  }
}

/** Where a posture document came from — `self` (device-reported) or `mdm` (VSNP-1165). */
export function postureSource(posture: Record<string, unknown> | null | undefined): string | null {
  const s = posture?.source;
  return typeof s === 'string' ? s : null;
}

/**
 * Flatten posture facts to scalar key→string for compact display. The
 * `source` tag is metadata about the document, not a fact, so it is shown
 * separately (postureSource) rather than counted here.
 */
export function postureFacts(posture: Record<string, unknown> | null | undefined): [string, string][] {
  if (!posture) return [];
  return Object.entries(posture)
    .filter(([k, v]) => k !== 'source' && v !== null && typeof v !== 'object')
    .map(([k, v]) => [k, String(v)] as [string, string]);
}

/**
 * The device's subject list, normalised for display.
 *
 * A device may be shared (design 23 §9.4 decision D), so "Owner" is really
 * "primary owner, of N subjects". Returning a plain array — empty when the
 * column is null — lets the caller show a count without having to know that
 * null and [] mean subtly different things on the server.
 */
export function deviceSubjects(device: Pick<AccessDevice, 'subjects'> | null | undefined): DeviceSubject[] {
  return Array.isArray(device?.subjects) ? device.subjects : [];
}

/** `kind:value` lines for a tooltip, one per subject. */
export function describeSubjects(subjects: DeviceSubject[]): string {
  if (subjects.length === 0) return 'No subjects — nobody may enrol this device until an admin assigns it';
  return subjects.map((s) => `${s.kind}: ${s.value}`).join('\n');
}

/**
 * The subjects that are NOT the primary owner — i.e. who else this device is
 * shared with.
 *
 * The raw count is the wrong number to show. An ordinary single-owner device is
 * seeded with TWO entries, the owner's `sub` and their email, so a column that
 * rendered `subjects.length` would announce "2 subjects" on every normal row
 * and mean nothing by it. What an administrator needs to see is the exception:
 * a device someone OTHER than its owner may also present.
 *
 * Matching is by value against the row's own owner fields, because that is what
 * makes an entry "the owner" — not its position in the list.
 */
export function sharedSubjects(
  device: Pick<AccessDevice, 'subjects' | 'user_email' | 'keycloak_sub'> | null | undefined,
): DeviceSubject[] {
  const email = device?.user_email?.trim().toLowerCase();
  const sub = device?.keycloak_sub?.trim();
  return deviceSubjects(device).filter((s) => {
    if (s.kind === 'sub') return !sub || s.value.trim() !== sub;
    return !email || s.value.trim().toLowerCase() !== email;
  });
}

/**
 * How a token-binding result should read to an administrator (WP8B).
 *
 * The raw enum values are wire constants shared with the gateway's metric
 * labels — `device_subject` is precise and unreadable. This maps each to a
 * short label, a severity, and the sentence that says what to DO about it,
 * which is the part a Devices page can actually act on: `device_subject` means
 * either a replay or an ownership record nobody updated, and an admin needs to
 * be told that it is the second one far more often than the first.
 *
 * `tone` drives colour only. It is deliberately NOT "good/bad", and `unbound`
 * is the reason: while Layer B was being built it was the expected state of
 * every session, so painting it red would have made the whole fleet look
 * broken and taught everyone to ignore the column. Now that the client proves
 * possession of its device key (WP8C), `unbound` means an OLD BUILD — a real
 * finding, but a rollout number rather than an incident, so it stays `info`
 * and the SENTENCE carries the change in meaning. The colour vocabulary
 * outlives the deployment state; the words do not have to.
 */
export interface TokenBindingDisplay {
  label: string;
  tone: 'ok' | 'info' | 'warn' | 'danger' | 'muted';
  detail: string;
}

export function tokenBindingDisplay(
  result: TokenBindingResult | string | null | undefined,
): TokenBindingDisplay {
  switch (result) {
    case 'bound':
      return {
        label: 'bound',
        tone: 'ok',
        detail:
          "The identity provider issued this token to this device's own key, and the gateway checked it against the certificate presented on the connection. A token stolen from this device cannot be used from another one.",
      };
    case 'unbound':
      return {
        label: 'unbound',
        tone: 'info',
        detail:
          'The token carried no confirmation claim, so nothing ties it to this device. Since Layer B this means an OLD CLIENT BUILD — a current one proves possession of its device key when it signs in. Still admitted in audit mode; refused under enforce.',
      };
    case 'mismatch':
      return {
        label: 'mismatch',
        tone: 'danger',
        detail:
          'The token was cryptographically bound to a DIFFERENT credential. This is never legitimate and is refused in every mode but off.',
      };
    case 'device_subject':
      return {
        label: 'wrong subject',
        tone: 'warn',
        detail:
          'The token named somebody who is not a subject of this device. Usually stale ownership — check who the device belongs to — but it is also what a replayed token looks like.',
      };
    case 'device_unassigned':
      return {
        label: 'unassigned',
        tone: 'warn',
        detail:
          'This device has no recorded subjects, so it carries nobody in particular. Reassign it to an owner before enforcement is turned on.',
      };
    default:
      return {
        label: '—',
        tone: 'muted',
        detail: 'Not observed yet — this device has not opened a session since subject checking shipped.',
      };
  }
}
